import type { CSSProperties, ReactNode } from "react";

import { cn } from "~/lib/cn";

/**
 * Highlight type for coach/session cards.
 * - "featured": Plain purple border (inset box-shadow)
 * - "founding": Gradient border with animation
 */
export type HighlightType = "featured" | "founding" | null;

const HIGHLIGHT_LABELS: Record<Exclude<HighlightType, null>, string> = {
  featured: "Featured Coach",
  founding: "Founding Coach",
};

interface HighlightBorderProps {
  type: HighlightType;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Show a ribbon label in the top-right corner. Defaults to true. */
  showLabel?: boolean;
}

/**
 * Wrapper component that adds a visual highlight border without affecting
 * the inner element's dimensions. Uses CSS techniques that don't impact layout:
 * - Featured: outward box-shadow
 * - Founding: pseudo-element gradient border extending outward
 *
 * @example
 * <HighlightBorder type="founding">
 *   <Card>...</Card>
 * </HighlightBorder>
 */
export function HighlightBorder({
  type,
  children,
  className,
  style,
  showLabel = true,
}: HighlightBorderProps): ReactNode {
  if (!type) {
    return children;
  }

  return (
    <div
      className={cn(
        "relative rounded-2xl",
        type === "featured" && "highlight-border-featured",
        type === "founding" && "highlight-border-founding bg-purple-50",
        className,
      )}
      style={style}
    >
      {children}
      {showLabel && (
        <div
          className={cn(
            "absolute -top-2 -right-2 z-10 rounded-md px-2 py-0.5 text-xs font-semibold text-white shadow-sm",
            type === "featured" && "bg-purple-500",
            type === "founding" &&
              "bg-linear-to-r from-purple-600 via-fuchsia-500 to-purple-600",
          )}
        >
          {HIGHLIGHT_LABELS[type]}
        </div>
      )}
    </div>
  );
}

/**
 * Derives the highlight type from coach status flags.
 */
export function deriveHighlightType(coach: {
  isFoundingCoach?: boolean | null;
}): HighlightType {
  if (coach.isFoundingCoach) return "founding";
  return null;
}
